import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  Rating
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';
import StarIcon from '@mui/icons-material/Star';

/**
 * General Feedback Form
 * Simple form with star rating and comment
 */
const GeneralFeedbackForm = ({ onSubmit, onCancel, isSubmitting }) => {
  const [formData, setFormData] = useState({
    star_rating: 0,
    title: '',
    description: ''
  });

  const [errors, setErrors] = useState({});
  const [hover, setHover] = useState(-1);

  const handleRatingChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      star_rating: newValue,
      title: getRatingTitle(newValue)
    }));

    // Clear error
    if (errors.star_rating) {
      setErrors(prev => ({ ...prev, star_rating: null }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const getRatingTitle = (rating) => {
    switch (rating) {
      case 1:
        return 'Poor Experience';
      case 2:
        return 'Below Expectations';
      case 3:
        return 'Good Experience';
      case 4:
        return 'Great Experience';
      case 5:
        return 'Excellent Experience';
      default:
        return '';
    }
  };

  const getRatingLabel = (value) => {
    const labels = {
      1: 'Poor',
      2: 'Fair',
      3: 'Good',
      4: 'Great',
      5: 'Excellent'
    };
    return labels[value];
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.star_rating || formData.star_rating === 0) {
      newErrors.star_rating = 'Please select a rating';
    }

    if (!formData.description || formData.description.trim().length === 0) {
      newErrors.description = 'Comment is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Comment must be at least 10 characters';
    } else if (formData.description.length > 2000) {
      newErrors.description = 'Comment must be less than 2000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    onSubmit(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={4}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            How would you rate your experience?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your feedback helps us improve TheWallet
          </Typography>
        </Box>

        {/* Star Rating */}
        <Box sx={{ textAlign: 'center' }}>
          <Rating
            name="star_rating"
            value={formData.star_rating}
            onChange={handleRatingChange}
            onChangeActive={(event, newHover) => {
              setHover(newHover);
            }}
            size="large"
            icon={<StarIcon fontSize="inherit" />}
            emptyIcon={<StarIcon fontSize="inherit" />}
            sx={{
              fontSize: '3rem',
              '& .MuiRating-iconFilled': {
                color: '#ffc107'
              },
              '& .MuiRating-iconHover': {
                color: '#ffb300'
              }
            }}
            disabled={isSubmitting}
          />

          {/* Rating label */}
          <Typography
            variant="body1"
            sx={{
              mt: 2,
              fontWeight: 600,
              color: formData.star_rating > 0 ? 'primary.main' : 'text.secondary',
              minHeight: '24px'
            }}
          >
            {formData.star_rating > 0
              ? getRatingLabel(hover !== -1 ? hover : formData.star_rating)
              : 'Select a rating'}
          </Typography>

          {errors.star_rating && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              {errors.star_rating}
            </Typography>
          )}
        </Box>

        {/* Comment */}
        <TextField
          fullWidth
          required
          multiline
          rows={6}
          label="Your Feedback"
          name="description"
          value={formData.description}
          onChange={handleChange}
          error={!!errors.description}
          helperText={errors.description || 'Tell us about your experience with TheWallet'}
          placeholder="What did you like? What could we improve? Any suggestions?"
          inputProps={{ maxLength: 2000 }}
          disabled={isSubmitting}
        />

        {/* Character count */}
        <Typography
          variant="caption"
          color={formData.description.length < 10 ? 'error' : 'text.secondary'}
          sx={{ textAlign: 'right', mt: -1 }}
        >
          {formData.description.length} / 10 minimum
        </Typography>

        {/* Thank you message */}
        {formData.star_rating >= 4 && (
          <Box
            sx={{
              p: 2,
              backgroundColor: 'success.lighter',
              borderRadius: 1,
              textAlign: 'center'
            }}
          >
            <Typography variant="body2" color="success.dark">
              ⭐ Thank you for the positive rating! We'd love to hear what you enjoy most.
            </Typography>
          </Box>
        )}

        {/* Action buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SendIcon />}
            disabled={isSubmitting}
          >
            Submit Feedback
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default GeneralFeedbackForm;

